import { Box, Avatar, Center, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { LayersThree02, Mail02, UserCircle } from '@kula-ai/icons-core';
import { type Candidate } from '@/pages/AISearch/types/index';
import { Link } from 'react-router-dom';
import { storeLocalSearchHistory } from '../../utils/search';
import { AI_SEARCH_STORAGE_KEYS } from '@/utils/storageKeys';
import { AISummary } from './AISummary';
import { useEffect, useRef, useMemo } from 'react';
import { AIDotSpinner } from '../AIDotSpinner';
import { COMMON_OPTIONS } from '@/components/filters_revamp/hooks/useCommonFilters/commonFiltersConfig';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

interface Job {
  job_id: string;
  job_name: string;
  status: string;
  application_id?: string;
}

interface CandidateProps {
  item: Candidate;
  isRecentViewed?: boolean;
  summaryInProgress?: boolean;
  isSelected?: boolean;
  jobs?: Job[];
  onSearchClose?: () => void;
}

export const CandidateItem = ({
  item = {} as any,
  isRecentViewed = false,
  summaryInProgress = false,
  isSelected = false,
  jobs = [],
  onSearchClose,
}: CandidateProps) => {
  const itemRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  const jobsList = useMemo(() => {
    return jobs.length > 0 ? (
      <Box mt="5px" className="ai-search-other-jobs">
        {jobs.map((job, index) => (
          <Flex
            key={index}
            as={Link}
            to={`/jobs/${job.job_id}?appId=${job.application_id}&tab=candidates`}
            alignItems="center"
            gap="10px"
            p="2px 8px"
            ml="-8px"
            borderRadius="6px"
            cursor="pointer"
            _hover={{ backgroundColor: 'white' }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onSearchClose?.();
            }}
            style={{ textDecoration: 'none' }}
          >
            <Center
              width="24px"
              height="24px"
              borderRadius="xs"
              bg="pastel-7-light"
              flexShrink={0}
            >
              <LayersThree02
                size={14}
                color="var(--kula-colors-pastel-7-dark)"
                className="kula-icon"
              />
            </Center>
            <KText size="xs-medium" color="primary-600" fontWeight="550">
              {job.job_name}
            </KText>
            <KText
              color="secondary-700"
              p="2px 4px"
              borderRadius="6px"
              border="1px solid"
              borderColor="secondary-100"
              size="sm-medium"
              fontSize="12px"
              bg="#fff"
            >
              {COMMON_OPTIONS.applicationStatusOptionsMap[job.status] ||
                job.status}
            </KText>
          </Flex>
        ))}
        <Flex
          as={Link}
          to={`/candidates/${item.id}/overview`}
          alignItems="center"
          gap="10px"
          p="4px 8px"
          ml="-8px"
          borderRadius="6px"
          cursor="pointer"
          _hover={{ backgroundColor: 'white' }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onSearchClose?.();
          }}
          style={{ textDecoration: 'none' }}
        >
          <Center
            width="24px"
            height="24px"
            borderRadius="xs"
            bg="pastel-3-light"
            flexShrink={0}
          >
            <UserCircle
              size={14}
              color="var(--kula-colors-pastel-3-dark)"
              className="kula-icon"
            />
          </Center>
          <KText size="xs-medium" color="primary-500" fontWeight="550">
            Global Contact Profile
          </KText>
        </Flex>
      </Box>
    ) : null;
  }, [jobs, item.id, onSearchClose]);

  return (
    <Link
      to={
        jobs.length > 0
          ? `/jobs/${jobs[0].job_id}?appId=${jobs[0].application_id}&tab=candidates`
          : `/candidates/${item.id}/overview`
      }
      style={{ textDecoration: 'none' }}
      ref={itemRef}
      onClick={() => {
        storeLocalSearchHistory(
          AI_SEARCH_STORAGE_KEYS.RECENT_VIEWED_CANDIDATES,
          item
        );
        onSearchClose?.();
      }}
    >
      <Box
        key={item.id}
        borderRadius="6px"
        p="12px"
        pt={isRecentViewed ? '12px' : '12px'}
        _hover={{
          backgroundColor: 'secondary-50',
          '.ai-search-summary-ellipsis': {
            background:
              'linear-gradient(to right, #efefefba, #F8F9FC 30%) !important',
          },
        }}
        backgroundColor={isSelected ? 'secondary-50' : undefined}
        cursor="pointer"
      >
        <Box>
          <Box display="inline-block" verticalAlign="top">
            <Avatar
              size="sm"
              src={item.avatar}
              name={item.name}
              borderRadius="6px"
              width="36px"
              height="36px"
            ></Avatar>
          </Box>
          <Box display="inline-block" verticalAlign="top" ml="12px" mt="-3px">
            <Box>
              {/* <KText
                size="sm-bold"
                color="secondary-900"
                fontWeight="650"
                ml="10px"
              >
                {item.name}
              </KText> */}
              <TruncatedTextWithTooltip
                color="secondary-900"
                size="sm-bold"
                text={item.name}
                enableTooltip={true}
                maxW="800px"
              />
            </Box>
            <Flex mt="2px">
              {item.position && (
                <KText size="sm-regular" color="secondary-900" mr="6px">
                  {item.position}
                </KText>
              )}
              <Flex alignItems="center" gap="6px">
                {item.position && (
                  <Flex width="1px" height="100%" bg="secondary-100"></Flex>
                )}
                <Mail02
                  size={16}
                  color="var(--kula-colors-secondary-700)"
                  className="kula-icon"
                />
                <KText size="sm-medium" color="secondary-700" mr="10px">
                  {item.email}
                </KText>
              </Flex>
            </Flex>
          </Box>
        </Box>
        {!isRecentViewed && (
          <>
            {summaryInProgress && <AIDotSpinner />}
            {!summaryInProgress && (
              <Box mt="5px">
                <AISummary summary={item.ai_summary || ''} />
              </Box>
            )}
          </>
        )}
        {jobsList}
      </Box>
    </Link>
  );
};
